/**
 * The programme's findings, in a drawer off the right edge.
 *
 * A drawer opens over the page rather than sharing a column with navigation,
 * so the findings are as wide as they need to be and the screen underneath
 * does not move.
 *
 * @module components/editor/FindingsDrawer
 */

import React, { useCallback, useRef, useState } from "react";

import { Button, Drawer } from "@/components/ui";

import { FindingsList } from "./FindingsList";

export interface FindingsDrawerProps {
  /** Opens the section that owns a finding, on its module when it names one. */
  onGoToStep: (stateKey: string, moduleId?: string) => void;
}

export function FindingsDrawer({ onGoToStep }: FindingsDrawerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [leftForSection, setLeftForSection] = useState(false);
  const [counts, setCounts] = useState({ errors: 0, warnings: 0 });
  const trigger = useRef<HTMLButtonElement>(null);

  const dismiss = useCallback(() => {
    setOpen(false);
  }, []);

  const navigateAndClose = useCallback(
    (stateKey: string, moduleId?: string) => {
      onGoToStep(stateKey, moduleId);
      setLeftForSection(true);
      setOpen(false);
      // A finding sent the reader somewhere else: pulling focus back to this
      // toolbar button would drop them back where they started instead of
      // wherever they asked to go. `#content` is the section the finding just
      // scrolled into view.
      requestAnimationFrame(() => {
        document.getElementById("content")?.focus();
      });
    },
    [onGoToStep],
  );

  const total = counts.errors + counts.warnings;

  return (
    <>
      <Button
        ref={trigger}
        variant="outline"
        size="sm"
        icon={counts.errors > 0 ? "warning-circle" : "check-circle"}
        onClick={() => {
          setLeftForSection(false);
          setOpen(true);
        }}
        aria-expanded={open}
        data-testid="findings-toggle"
      >
        Findings ({total})
      </Button>

      {/* Mounted whether or not it is open, so the counts it reports are
          current before anyone opens it: the trigger's own number comes from
          the list inside. */}
      <Drawer
        open={open}
        onClose={dismiss}
        title={`Findings (${total})`}
        aria-label="Validation findings"
        closeLabel="Close findings"
        returnFocusTo={trigger}
        restoreFocus={!leftForSection}
        data-testid="findings-drawer"
        backdropTestId="findings-backdrop"
      >
        <FindingsList onGoToStep={navigateAndClose} onCountsChange={setCounts} />
      </Drawer>
    </>
  );
}
